<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Danh sách nhóm hoa hồng'">
          <template v-slot:toolbar>
            <div class="row">
              <div class="ml-3">
                <router-link to="/commission/upsert-category">
                  <button
                    type="button"
                    class="btn btn-primary font-weight-bolder btn-sm"
                  >
                    <i style="font-size: 1rem" class="flaticon2-add-1"></i>Thêm
                    mới
                  </button>
                </router-link>
              </div>
            </div>
          </template>
          <template v-slot:preview>
            <!-- Bộ lọc -->
            <div class="col-md-12 mb-5 pl-0">
              <b-row>
                <b-col md="3" class="pl-0">
                  <b-input
                    size="sm"
                    placeholder="Tìm theo tên nhóm / mô tả"
                    v-model="filter.name"
                    autocomplete="off"
                    v-on:input="debounceSearch"
                  ></b-input>
                </b-col>
                <b-col md="2">
                  <b-form-select
                    size="sm"
                    v-model="filter.type"
                    :options="typeOptions"
                    value-field="id"
                    text-field="name"
                    @change="onFilter"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null"
                        >-- Tất cả loại nhóm --</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-col>
                <b-col md="6">
                  <CommissionItemSearch
                    ref="item-search"
                    input-id="commission-category-filter__input"
                    :clear-on-select="false"
                    @selected="onSelectedItem"
                    @change-type="clearLookup"
                  />
                </b-col>
                <b-col md="1">
                  <b-button
                    style="width: 70px"
                    variant="primary"
                    size="sm"
                    @click="onFilter"
                    >Lọc</b-button
                  >
                </b-col>
              </b-row>
            </div>

            <!-- Đang lọc theo sản phẩm / danh mục -->
            <div v-if="lookupTarget" class="lookup-bar mb-4">
              <div>
                <div>
                  Nhóm có chứa
                  <strong>{{ lookupTarget.name }}</strong>
                  <span v-if="lookupTarget.code" class="text-muted"
                    >({{ lookupTarget.code }})</span
                  >
                </div>
                <small class="text-muted">
                  Danh mục: {{ lookupTarget.categoryPath || '--' }}
                </small>
              </div>
              <b-button size="sm" variant="light" @click="clearLookup">
                <i class="fas fa-times mr-1"></i>Bỏ lọc
              </b-button>
            </div>

            <!-- Bảng dữ liệu -->
            <b-table
              :items="tableItems"
              :fields="tableFields"
              bordered
              hover
              :busy="onLoading"
              show-empty
              :empty-text="
                lookupTarget
                  ? 'Không nằm trong nhóm hoa hồng nào'
                  : 'Không có nhóm hoa hồng nào'
              "
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="8"></vcl-table>
              </template>
              <template v-slot:cell(name)="row">
                <span>{{ row.item.name }}</span>
                <b-badge
                  v-if="row.item.isPartial"
                  variant="warning"
                  class="ml-2"
                  v-b-tooltip.hover
                  title="Nhóm chỉ chứa một vài danh mục con"
                  >Một phần</b-badge
                >
              </template>
              <template v-slot:cell(matches)="row">
                <div v-for="(match, index) in row.item.matches" :key="index">
                  <span class="text-muted">{{ match.match }}:</span>
                  {{ match.name }}
                </div>
              </template>
              <template v-slot:cell(type)="row">
                <span>{{ typeName(row.item.type) }}</span>
              </template>
              <template v-slot:cell(totalItem)="row">
                <span v-if="row.item.type === CATEGORY_TYPE.PRODUCT">
                  {{ row.item.totalItem || 0 }}
                </span>
                <span v-else class="text-muted">--</span>
              </template>
              <template v-slot:cell(createdAt)="row">
                <span>{{ row.item.createdAt }}</span>
              </template>
              <template v-slot:cell(actions)="row">
                <div class="d-flex justify-content-center">
                  <b-dropdown size="sm" no-caret right>
                    <template slot="button-content">
                      <i
                        style="font-size: 1rem; padding-right: 0px"
                        class="flaticon2-settings"
                      ></i>
                    </template>
                    <b-dropdown-item @click="editItem(row.item)">
                      <span style="color: #3f4254; font-size: 12px">
                        <i style="font-size: 1rem" class="flaticon2-pen"></i>
                        &nbsp; Chỉnh sửa
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="showHistory(row.item)">
                      <span style="color: #3f4254; font-size: 12px">
                        <i style="font-size: 1rem" class="fas fa-history"></i>
                        &nbsp; Xem lịch sử
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-item
                      @click="showDeleteAlert(row.item)"
                      v-if="checkPermission('REWARD_DELETE')"
                    >
                      <span style="color: #3f4254; font-size: 12px">
                        <i
                          style="font-size: 1rem; color: #d33"
                          class="flaticon2-rubbish-bin-delete-button"
                        ></i>
                        &nbsp; Xóa
                      </span>
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>
            </b-table>

            <!-- Phân trang -->
            <b-row>
              <b-col>
                <p class="mt-3 text-dark" style="font-weight: 500">
                  <template v-if="lookupTarget">
                    Tìm thấy {{ tableItems.length }} nhóm
                  </template>
                  <template v-else>
                    Tổng số nhóm hoa hồng: {{ totalItems }}
                  </template>
                </p>
              </b-col>
              <b-col>
                <b-pagination-nav
                  class="custom-pagination"
                  v-show="!lookupTarget && totalPages >= 2"
                  :link-gen="linkGen"
                  :number-of-pages="totalPages"
                  use-router
                  @change="fetchData"
                  align="right"
                  first-class="page-item-first btn btn-icon btn-sm mr-1 my-1"
                  prev-class="page-item-prev btn btn-icon btn-sm mr-.5 my-1"
                  next-class="page-item-next btn btn-icon btn-sm mr-1 my-1 ml-.5"
                  last-class="page-item-last btn btn-icon btn-sm my-1"
                  page-class="btn btn-icon btn-sm border-0 mr-.5 my-1"
                >
                  <template v-slot:first-text>
                    <span>
                      <i class="ki ki-bold-double-arrow-back icon-xs"></i>
                    </span>
                  </template>
                  <template v-slot:prev-text>
                    <i class="ki ki-bold-arrow-back icon-xs"></i>
                  </template>
                  <template v-slot:next-text>
                    <i class="ki ki-bold-arrow-next icon-xs"></i>
                  </template>
                  <template v-slot:last-text>
                    <span class="text-info">
                      <i class="ki ki-bold-double-arrow-next icon-xs"></i>
                    </span>
                  </template>
                </b-pagination-nav>
              </b-col>
            </b-row>
          </template>
        </KTCodePreview>
      </div>
    </div>
    <HistoryModal
      ref="history-modal"
      modal-id="commission-category-history-modal"
      title="Lịch sử thay đổi nhóm hoa hồng"
    />
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import Swal from 'sweetalert2';
import decounce from 'debounce';
import { VclTable } from 'vue-content-loading';
import localData from '@/utils/saveDataToLocal';
import { TIME_TRIGGER } from '@/utils/constants';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import HistoryModal from './HistoryModal.vue';
import CommissionItemSearch from './CommissionItemSearch.vue';
import {
  COMMISSION_CATEGORY_TYPE,
  COMMISSION_CATEGORY_TYPE_NAME,
  toOptionList,
} from './commission.constants';
import commissionReportMixin, { thStyle } from './commissionReport.mixin';

const PAGE_LIMIT = 10;

export default {
  components: {
    KTCodePreview,
    VclTable,
    HistoryModal,
    CommissionItemSearch,
  },
  mixins: [commissionReportMixin],
  data() {
    return {
      CATEGORY_TYPE: COMMISSION_CATEGORY_TYPE,
      onLoading: false,
      items: [],
      totalPages: 0,
      totalItems: 0,
      page: 1,
      filter: {
        name: '',
        type: null,
      },
      typeOptions: toOptionList(COMMISSION_CATEGORY_TYPE_NAME),
      // Lọc theo sản phẩm / danh mục: kết quả tra ngược, lọc tên / loại ngay trên trình duyệt
      lookupTarget: null,
      lookupGroups: [],
      lookupSeq: 0,
      lookupFields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: thStyle('5%'),
          tdClass: 'text-center',
        },
        { key: 'name', label: 'Tên nhóm', thStyle: thStyle('30%') },
        { key: 'type', label: 'Loại nhóm', thStyle: thStyle('15%') },
        { key: 'matches', label: 'Khớp theo', thStyle: thStyle('45%') },
        { key: 'actions', label: '', thStyle: thStyle('5%') },
      ],
      linkGen: (pageNum) => (pageNum === 1 ? '?' : `?page=${pageNum}`),
      fields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
          tdClass: 'text-center',
        },
        {
          key: 'name',
          label: 'Tên nhóm',
          thStyle: { fontWeight: 600, color: '#181c32', width: '22%' },
        },
        {
          key: 'type',
          label: 'Loại nhóm',
          thStyle: { fontWeight: 600, color: '#181c32', width: '13%' },
        },
        {
          key: 'totalItem',
          label: 'Số lượng item',
          thStyle: { fontWeight: 600, color: '#181c32', width: '10%' },
          tdClass: 'text-center',
        },
        {
          key: 'description',
          label: 'Mô tả',
          thStyle: { fontWeight: 600, color: '#181c32', width: '25%' },
        },
        {
          key: 'createdAt',
          label: 'Ngày tạo',
          thStyle: { fontWeight: 600, color: '#181c32', width: '10%' },
        },
        {
          key: 'createdByName',
          label: 'Người tạo',
          thStyle: { fontWeight: 600, color: '#181c32', width: '10%' },
        },
        {
          key: 'actions',
          label: '',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
        },
      ],
    };
  },
  computed: {
    tableFields() {
      return this.lookupTarget ? this.lookupFields : this.fields;
    },
    tableItems() {
      if (!this.lookupTarget) {
        return this.items;
      }
      const keyword = (this.filter.name || '').trim().toLowerCase();
      return this.lookupGroups
        .filter(
          (group) =>
            (!keyword || group.name.toLowerCase().includes(keyword)) &&
            (!this.filter.type || group.type === this.filter.type),
        )
        .map((group, index) => ({ ...group, count: index + 1 }));
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng V2' },
      { title: 'Danh sách nhóm hoa hồng' },
    ]);
  },
  created() {
    this.fetchData();
  },
  methods: {
    checkPermission(condition) {
      return localData.checkPermission(condition);
    },
    typeName(type) {
      return COMMISSION_CATEGORY_TYPE_NAME[type] || '';
    },
    debounceSearch: decounce(function () {
      this.onFilter();
    }, TIME_TRIGGER),
    onFilter() {
      // Đang lọc theo sản phẩm / danh mục: tên + loại lọc ngay trên kết quả (computed)
      if (this.lookupTarget) {
        return;
      }
      if (this.$route.query.page !== undefined) {
        this.$router.replace({ query: {} }).catch(() => {});
      }
      this.fetchData();
    },
    fetchData() {
      this.onLoading = true;
      this.page = Number(this.$route.query.page) || 1;

      const params = {
        page: this.page,
        limit: PAGE_LIMIT,
        name: this.filter.name || undefined,
        type: this.filter.type || undefined,
      };

      ApiService.setHeader();
      ApiService.query('commission-categories', { params })
        .then(({ data }) => {
          const result = data.data || {};
          const list = result.data || [];
          this.totalPages = result.totalPage || result.total_page || 0;
          this.totalItems = result.totalRow || result.total_row || 0;
          const offset = (this.page - 1) * PAGE_LIMIT;
          this.items = list.map((item, index) => ({
            ...item,
            count: offset + index + 1,
          }));
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được danh sách nhóm hoa hồng!'),
          );
        })
        .finally(() => {
          this.onLoading = false;
        });
    },
    editItem(item) {
      this.$router.push({
        name: 'commission-upsert-category',
        query: { id: item.id },
      });
    },
    onSelectedItem({ itemType, item }) {
      this.lookupSeq += 1;
      const seq = this.lookupSeq;
      this.onLoading = true;
      ApiService.setHeader();
      ApiService.query('commission-categories/lookup', {
        params: { type: itemType, referenceId: item.id },
      })
        .then(({ data }) => {
          if (seq !== this.lookupSeq) {
            return;
          }
          const result = data.data || {};
          this.lookupTarget = result.target || null;
          this.lookupGroups = result.groups || [];
        })
        .catch((error) => {
          if (seq === this.lookupSeq) {
            makeToastFaile(this.getErrorMessage(error, 'Tra cứu thất bại!'));
          }
        })
        .finally(() => {
          if (seq === this.lookupSeq) {
            this.onLoading = false;
          }
        });
    },
    clearLookup() {
      this.lookupSeq += 1;
      const hadLookup = Boolean(this.lookupTarget);
      this.lookupTarget = null;
      this.lookupGroups = [];
      this.onLoading = false;
      this.$refs['item-search'].clear();
      if (hadLookup) {
        // Lọc tên / loại trong lúc tra cứu có thể làm trang cũ không còn dữ liệu
        if (this.$route.query.page !== undefined) {
          this.$router.replace({ query: {} }).catch(() => {});
        }
        this.fetchData();
      }
    },
    showHistory(item) {
      this.$refs['history-modal'].show(
        `commission-categories/${item.id}/histories`,
      );
    },
    showDeleteAlert(item) {
      Swal.fire({
        title: 'Xóa nhóm hoa hồng',
        text: `Bạn có chắc muốn xóa nhóm hoa hồng "${item.name}" không?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        position: 'top',
      }).then((result) => {
        if (result.value) {
          this.deleteItem(item);
        }
      });
    },
    deleteItem(item) {
      ApiService.setHeader();
      ApiService.delete(`commission-categories/${item.id}`).then((response) => {
        // ApiService.delete đã tự toast lỗi và trả về đối tượng error khi thất bại
        if (!response || !response.data) {
          return;
        }
        makeToastSuccess(
          response.data.message || 'Xóa nhóm hoa hồng thành công!',
        );
        // Đang tra cứu thì bảng vẽ từ kết quả tra cứu — phải tra lại mới mất nhóm vừa xoá
        if (this.lookupTarget) {
          this.onSelectedItem({
            itemType: this.lookupTarget.type,
            item: { id: this.lookupTarget.referenceId },
          });
          return;
        }
        this.fetchData();
      });
    },
  },
};
</script>

<style scoped>
.lookup-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: #f1faff;
  border: 1px solid #cfe7fb;
  border-radius: 6px;
}
.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
  border-top: 1px solid #ecf0f3;
}
</style>
